import * as React from "react";
import FlagSharpIcon from "@material-ui/icons/FlagSharp";
import { Release } from "../../../../../../../../common/types/release/release";
import SimpleArtist from "../../../../../../../../common/types/artist/simple-artist";
import styled from "styled-components";
import { getReleaseOwnerArtist } from "../../../../../utils/get-owner-artist";
import Chip from "@material-ui/core/Chip";
import Link from "@material-ui/core/Link";
import ReplayIcon from "@material-ui/icons/Replay";
import { routes } from "../../../../../routes";
import { Subscription } from "../../../../../../../../services/subscription/types/subscription";
import { ToolTip } from "../../../../../components/tooltip";
import { Alert } from "../../../../../components/alert";
import { replace } from "../../../../../sdk/assets";

interface ImageData {
  src: string;
  width: number;
  height: number;
  type: string;
}

interface ReleaseDetailHeaderProps {
  release: Release;
  subscription?: Subscription;
  artists: SimpleArtist[];
  onUpdateField: <T extends keyof Release>(key: T, value: Release[T]) => void;
}

const promiseDelay = (t: number) => new Promise((resolve) => setTimeout(resolve, t));

export const ReleaseDetailHeader: React.FC<ReleaseDetailHeaderProps> = (
  props
) => {
  const [showAlert, setShowAlert] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState<string>('');
  const { release, subscription, artists } = props;
  const enabled: boolean = !release.artists.some(
    (val) => val.enabled === false
  );
  const watchlistNote: string | undefined = artists.find(
    (artist) => artist.watchlistNote
  )?.watchlistNote;

  let plan = subscription?.plan || "Beginner";
  if (!subscription?.isActive) {
    plan = `${plan} (INACTIVE)`;
  }

  const readImage = (f: File): Promise<ImageData> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target && e.target.result) {
          const img = new Image();
          const src = e.target.result.toString();
          img.onload = () => {
            resolve({
              src,
              width: img.width,
              height: img.height,
              type: f.type,
            });
          };
  
          img.onerror = () => reject(new Error('Error while loading file'));
          img.src = src;
        }
      };
      reader.readAsDataURL(f);
    });
  };

  const validateImageFormat = (file: File) => {
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      setShowAlert(true);
      setAlertMessage("Invalid Image Format For Cover Art!");
    }
  };

  const validateImageSize = (image: ImageData) => {
    if (
      image.width < 3000 ||
      image.height < 3000 ||
      image.width > 6000 ||
      image.height > 6000 ||
      image.width !== image.height
    ) {
      setShowAlert(true);
      setAlertMessage("Invalid Image Dimension!");
    }
  };

  const onCoverArtChange = async(event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return;
    }
    const file = event.target.files[0];
    if (file.size > 10485760) {
      setShowAlert(true);
      setAlertMessage("The Size of the Cover Art exceeds 10Mb!");
      return;
    }

    try {
      validateImageFormat(file);

      const image = await readImage(file);

      validateImageSize(image);

      const newRequest = await replace(release.id, 'artwork', file);

      await newRequest.upload.request;

      await promiseDelay(1500);

      props.onUpdateField('cover', {
        url: newRequest.url,
        id: newRequest.id,
        contentType: file.type,
        size: file.size,
        crc32: '',
        key: newRequest.key,
        created_at: new Date(),
        updated_at: new Date(),
      });
    } catch (e) {
      setShowAlert(true);
      setAlertMessage("Unknown Error Occurred When Replacing Uploading Images!");
    }
  };

  return (
    <HeaderContainer>
      <EmptyDiv>
        <div style={{position: "relative"}}>
          <CoverArt src={release.cover.url} style={{display: "block"}} />
          <div style={{position: "absolute", bottom: 20, right: 15}}>
            <input 
              type="file" 
              id="file" 
              hidden={true} 
              accept="image/*"
              multiple={false} 
              onChange={(e) => onCoverArtChange(e)} 
            />
            <label htmlFor="file"><ReplayIcon /></label>
          </div>
        </div>
        <HeaderInformation>
          <AlbumTitle>{release.title}</AlbumTitle>
          <AlbumArtist>
            <Link
              color="inherit"
              href={`${routes.artist.list}/${
                getReleaseOwnerArtist(release)?.id
              }`}
            >
              {getReleaseOwnerArtist(release)?.name || ""}
            </Link>{" "}
            / {release.recordLabel}
          </AlbumArtist>
          {plan ? (
            <Chip
              size="small"
              label={plan}
              color={plan.includes("Pro") ? "primary" : "default"}
            />
          ) : (
            <></>
          )}
          <IdContainer>UPC: {release.UPCEAN}</IdContainer>
          <IdContainer>Internal: {release.id}</IdContainer>
          <IdContainer>Status: {release.status}</IdContainer>
          <IdContainer>
            Last action taken: {release.backofficeStatus}
          </IdContainer>
        </HeaderInformation>
      </EmptyDiv>
      {watchlistNote && (
        <ToolTip helperText={watchlistNote}>
          <FlagSharpIcon color="error" fontSize="large" />
        </ToolTip>
      )}
      {!enabled && <Chip size="small" label={"Blocked"} color="secondary" />}
      <Alert 
        isOpen={showAlert} 
        title="An Error Occurred!" 
        message={alertMessage} 
        onClose={() => setShowAlert(false)}
      />
    </HeaderContainer>
  );
};

const HeaderContainer = styled.header`
  display: flex;
  margin-top: 20px;
  justify-content: space-between;
`;

const CoverArt = styled.img`
  width: 180px;
  height: 180px;
  margin-right: 1rem;
  border-radius: 4px;
`;

const HeaderInformation = styled.div``;

const EmptyDiv = styled.div`
  display: flex;
`;

const AlbumTitle = styled.h2`
  margin: 0 0 5px;
`;

const AlbumArtist = styled.h3`
  margin: 0 0 20px;
`;

const IdContainer = styled.p`
  margin: 0 0 5px;
`;
